import { Injectable } from '@angular/core';
import { AreaItemPack } from '../models/area-item-pack';
import { BookingService } from './booking.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AreaItem } from '../models/area-item';
import { TimeRestriction } from '../models/time-restriction';
import { BookingSlot } from '../models/booking-slot';
import { ConnectionService } from '../../../services/connection/connection.service';
import { GroupEventOrder } from '../../order/models/group-event-order';
import { ContactField } from '../../contact/models/contact-field';
import { environment } from '../../../../environments/environment';
import { EventAreaItemSlot } from '../models/event-area-item-slot';
import { ShopSettingsService } from '../../general/services/shop-settings.service';

@Injectable()
export class BookingOdooService implements BookingService {

  constructor(
    private connectionService: ConnectionService,
    private shopSettingsService: ShopSettingsService,
  ) {
  }

  getAreaItems(areaItemPackId: number): Observable<AreaItem[]> {
    return this.connectionService.sendJsonPostRequest('/store/area/area-items', {
      id: areaItemPackId
    }).pipe(map(result => {
      if (!result) {
        return null;
      }

      return result.map(areaItem => AreaItem.createFromAny(areaItem));
    }));
  }

  getAllAreaItems(): Observable<AreaItem[]> {
    return this.connectionService.sendJsonPostRequest('/store/area/all-area-items', {
    }).pipe(map(result => {
      if (!result) {
        return null;
      }

      return result.map(areaItem => AreaItem.createFromAny(areaItem));
    }));
  }

  getAreaItemPacks(): Observable<AreaItemPack[]> {
    return this.connectionService.sendJsonPostRequest('/store/area/area-items-packs', {
      profile: this.shopSettingsService.profile,
    })
      .pipe(map(result => {
        if (!result) {
          return null;
        }

        return result.map(areaItem => AreaItemPack.createFromAny(areaItem));
      }));
  }

  getAreaRestrictions(areaItemPackId: number): Observable<TimeRestriction[]> {
    return this.connectionService.sendJsonPostRequest('/store/area/time-restrictions', {
      areaItemPackId,
    }).pipe(map(
      result => {
        if (!result) {
          return null;
        }

        return result.map(x => TimeRestriction.createFromAny(x));
      }
    ));
  }

  getBookingSlots(areaItemPackId: number, date: Date): Observable<BookingSlot[]> {
    date = environment.globalSettings.getIsoDateStrByDate(date);
    const isKioskMode = this.shopSettingsService.isVUMode;
    return this.connectionService.sendJsonPostRequest('/store/area/booking-slots', {
      areaItemPackId,
      bookingDate: date,
      isKioskMode,
    }).pipe(map(
      result => {
        if (!result) {
          return null;
        }

        return result.map(x => BookingSlot.createFromAny(x));
      }
    ));
  }

  getEventBookingSlots(areaItem: AreaItem, date: Date): Observable<BookingSlot[]> {
    date = environment.globalSettings.getIsoDateStrByDate(date);
    const productId = areaItem.productId;
    return this.connectionService.sendJsonPostRequest('/store/area/event-booking-slots', {
      productId,
      bookingDate: date,
    }).pipe(map(
      result => {
        if (!result) {
          return null;
        }

        return result.map(x => BookingSlot.createFromAny(x));
      }
    ));
  }

  sendGroupEventOrder(groupEventOrder: GroupEventOrder): Observable<boolean> {
    return this.connectionService.sendJsonPostRequest('/store/order/send-group-event-order', groupEventOrder);
  }

  sendContactForm(contactField: ContactField): Observable<boolean> {
    return this.connectionService.sendJsonPostRequest('/store/company/send-contact-form', contactField);
  }

  getEventAreaItemSlots(): Observable<EventAreaItemSlot[]> {
    const isKioskMode = this.shopSettingsService.isVUMode;
    return this.connectionService.sendJsonPostRequest('/store/area/event-area-item-slots', {
      isKioskMode,
    })
      .pipe(map(result => {
        if (!result) {
          return null;
        }

        return result.map(eventAreaItemSlot => EventAreaItemSlot.createFromAny(eventAreaItemSlot));
      }));
  }


  getAvailableTicketQuantity(): Observable<number> {
    const isKioskMode = this.shopSettingsService.isVUMode;
    return this.connectionService.sendJsonPostRequest('/store/area/available-ticket-quantity', {
      isKioskMode,
    });
  }
}
